import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useLanguage } from "./i18n/LanguageContext";
import { selfPersonLabel } from "./lib/selfPersonLabel";

const API = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : "/api";

const ACTIVE_SUBJECT_KEY = "mc_active_subject";

const SubjectContext = createContext(null);

export const useSubjects = () => {
  const ctx = useContext(SubjectContext);
  if (!ctx) throw new Error("useSubjects must be used within SubjectProvider");
  return ctx;
};

function readStoredSubjectId() {
  try {
    return localStorage.getItem(ACTIVE_SUBJECT_KEY) || null;
  } catch {
    return null;
  }
}

function writeStoredSubjectId(subjectId) {
  try {
    if (subjectId) localStorage.setItem(ACTIVE_SUBJECT_KEY, subjectId);
    else localStorage.removeItem(ACTIVE_SUBJECT_KEY);
  } catch {
    // ignore
  }
}

export const SubjectProvider = ({ user, children }) => {
  const { t } = useLanguage();
  const [subjects, setSubjects] = useState([]);
  const [limit, setLimit] = useState(8);
  const [activeSubjectId, setActiveSubjectIdState] = useState(readStoredSubjectId);
  const [caseOneOff, setCaseOneOff] = useState(false);
  const [loading, setLoading] = useState(false);

  const refreshSubjects = useCallback(async () => {
    if (!user) {
      setSubjects([]);
      return [];
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/subjects`);
      const items = res.data?.data?.items || [];
      const lim = res.data?.data?.limit ?? 8;
      setSubjects(items);
      setLimit(lim);
      const stored = readStoredSubjectId();
      const stillExists = items.some((s) => s.subject_id === stored);
      if (!stillExists) {
        const self = items.find((s) => s.kind === "self") || items[0];
        const nextId = self?.subject_id || null;
        setActiveSubjectIdState(nextId);
        writeStoredSubjectId(nextId);
      }
      return items;
    } catch {
      setSubjects([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshSubjects();
  }, [refreshSubjects]);

  const setActiveSubjectId = useCallback((subjectId) => {
    setCaseOneOff(false);
    setActiveSubjectIdState(subjectId);
    writeStoredSubjectId(subjectId);
  }, []);

  const selectOneOffForCase = useCallback(() => {
    setCaseOneOff(true);
  }, []);

  const clearCaseOneOff = useCallback(() => {
    setCaseOneOff(false);
  }, []);

  const visibleSubjects = useMemo(() => {
    const meLabel = t("subjects.me");
    const accountName = user?.name || "";
    return subjects.map((s) => {
      if (s.kind !== "self") return s;
      const label = selfPersonLabel(s.display_name, meLabel, accountName);
      if (label === s.display_name) return s;
      return { ...s, display_name: label };
    });
  }, [subjects, t, user?.name]);

  const activeSubject = useMemo(() => {
    if (caseOneOff) return null;
    return (
      visibleSubjects.find((s) => s.subject_id === activeSubjectId) ||
      visibleSubjects[0] ||
      null
    );
  }, [visibleSubjects, activeSubjectId, caseOneOff]);

  const value = useMemo(
    () => ({
      subjects: visibleSubjects,
      limit,
      loading,
      activeSubject,
      activeSubjectId,
      caseOneOff,
      setActiveSubjectId,
      selectOneOffForCase,
      clearCaseOneOff,
      refreshSubjects,
      atLimit: subjects.length >= limit,
    }),
    [
      visibleSubjects,
      subjects.length,
      limit,
      loading,
      activeSubject,
      activeSubjectId,
      caseOneOff,
      setActiveSubjectId,
      selectOneOffForCase,
      clearCaseOneOff,
      refreshSubjects,
    ],
  );

  return <SubjectContext.Provider value={value}>{children}</SubjectContext.Provider>;
};
